import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ChatCircleDots, Plus, PaperPlaneRight, Users, Books, Buildings, User, Bell, Buildings as BuildingsIcon, ShieldStar, Lock,
} from "@phosphor-icons/react";

const SCOPE_META = {
  administration: { label: "Administration",  icon: BuildingsIcon, color: "bg-primary text-primary-foreground", desc: "Écrire à la direction" },
  class:          { label: "Classe",           icon: Users,        color: "bg-secondary text-secondary-foreground", desc: "Toute la classe" },
  course:         { label: "Cours",            icon: Books,        color: "bg-accent text-accent-foreground", desc: "Enseignant + élèves" },
  announcement:   { label: "Annonce",          icon: Bell,         color: "bg-destructive text-destructive-foreground", desc: "Diffusion école (lecture seule)" },
  school:         { label: "École",            icon: Buildings,    color: "bg-muted text-foreground", desc: "Tous les membres" },
  direct:         { label: "Message direct",   icon: User,         color: "bg-muted-foreground/20 text-foreground", desc: "1-à-1" },
};

export default function Messagerie() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [open, setOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [form, setForm] = useState({ scope: "administration", ref_id: "", title: "", include_guardians: true });
  const listRef = useRef(null);

  const canCreateAny = scopes.some(s => s.can_create);

  const loadThreads = async () => {
    try { const r = await api.get("/threads"); setThreads(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadCtx = async () => {
    try {
      const [cr, cl, sc] = await Promise.all([
        api.get("/courses").catch(() => ({ data: [] })),
        api.get("/classes").catch(() => ({ data: [] })),
        api.get("/thread-scopes"),
      ]);
      setCourses(cr.data); setClasses(cl.data); setScopes(sc.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadThreads(); loadCtx(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      try {
        const r = await api.get(`/threads/${selected.id}/messages`);
        setMessages(r.data.messages);
        await api.post(`/threads/${selected.id}/read`);
        setThreads(prev => prev.map(t => t.id === selected.id ? { ...t, unread: false, read_by: [...(t.read_by || []), user.id] } : t));
        setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 100);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [selected?.id, user?.id]);

  const readonlyForMe = selected?.readonly
    && selected?.created_by !== user?.id
    && !["super_admin","owner","admin","director","study_director"].includes(user?.role);

  const send = async () => {
    if (!body.trim() || !selected || readonlyForMe) return;
    try {
      const r = await api.post(`/threads/${selected.id}/messages`, { thread_id: selected.id, body });
      setMessages(prev => [...prev, r.data]);
      setBody("");
      setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 50);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const create = async () => {
    try {
      const payload = { ...form };
      if (!["course","class"].includes(payload.scope)) payload.ref_id = null;
      if (!payload.title.trim()) { toast.error("Titre requis"); return; }
      const r = await api.post("/threads", payload);
      toast.success("Fil créé");
      setOpen(false);
      setForm({ scope: "administration", ref_id: "", title: "", include_guardians: true });
      loadThreads(); setSelected(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clById = useMemo(() => Object.fromEntries(classes.map(c => [c.id, c])), [classes]);

  const availableScopes = scopes.filter(s => s.can_create);
  const currentScopeMeta = SCOPE_META[form.scope] || SCOPE_META.direct;
  const CurrentIcon = currentScopeMeta.icon;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Communication</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <ChatCircleDots size={32} weight="duotone" className="text-primary" />
            Messagerie
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Administration, classe, cours, annonces — communiquez avec la bonne portée.</p>
        </div>
        {canCreateAny && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-thread">
                <Plus size={16} className="mr-2" /> Nouveau fil
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CurrentIcon size={20} weight="duotone" className="text-primary" />
                  Nouveau fil de discussion
                </DialogTitle>
                <DialogDescription>Choisissez la portée — les participants seront ajoutés automatiquement.</DialogDescription>
              </DialogHeader>

              {/* Visual scope picker */}
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Portée</Label>
                <div className="grid grid-cols-2 gap-2 mt-2" data-testid="scope-picker">
                  {availableScopes.map(s => {
                    const M = SCOPE_META[s.key] || SCOPE_META.direct;
                    const Icon = M.icon;
                    const selected = form.scope === s.key;
                    return (
                      <button key={s.key} type="button"
                              onClick={() => setForm({ ...form, scope: s.key, ref_id: "" })}
                              className={`text-left p-3 rounded-lg border-2 transition-all ${selected ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "border-border hover:border-primary/40"}`}
                              data-testid={`scope-option-${s.key}`}>
                        <div className="flex items-start gap-2">
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${M.color}`}>
                            <Icon size={16} weight="duotone" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm">{s.label}</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{s.description}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid gap-3 mt-4">
                {form.scope === "course" && (
                  <div>
                    <Label>Cours</Label>
                    <Select value={form.ref_id} onValueChange={v => setForm({ ...form, ref_id: v })}>
                      <SelectTrigger data-testid="select-thread-course"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>{courses.map(c => {
                        const cl = clById[c.class_group_id];
                        return <SelectItem key={c.id} value={c.id}>{(c.subject_id||"?").slice(0,6)} · {cl?.name || "?"}</SelectItem>;
                      })}</SelectContent>
                    </Select>
                  </div>
                )}
                {form.scope === "class" && (
                  <div>
                    <Label>Classe</Label>
                    <Select value={form.ref_id} onValueChange={v => setForm({ ...form, ref_id: v })}>
                      <SelectTrigger data-testid="select-thread-class"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Titre</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                         data-testid="input-thread-title"
                         placeholder={form.scope === "announcement" ? "Ex: Fermeture exceptionnelle vendredi" : "Ex: Rappel devoir de mathématiques"} />
                </div>
                {(form.scope === "course" || form.scope === "class") && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.include_guardians} onChange={e => setForm({ ...form, include_guardians: e.target.checked })} />
                    Inclure les parents/tuteurs
                  </label>
                )}
                {form.scope === "announcement" && (
                  <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md p-2 flex gap-2">
                    <Lock size={14} className="shrink-0 mt-0.5" />
                    Le fil sera diffusé à toute l'école en lecture seule — seule la direction pourra y répondre.
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={create} data-testid="btn-save-thread">Créer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[520px]">
        {/* Threads list */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Fils ({threads.length})</div>
          {threads.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">Aucun fil. {canCreateAny ? "Créez-en un." : "Attendez qu'un message vous soit adressé."}</Card>
          ) : threads.map(t => {
            const M = SCOPE_META[t.scope] || SCOPE_META.direct;
            const Icon = M.icon;
            return (
              <button key={t.id} onClick={() => setSelected(t)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${selected?.id === t.id ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:border-primary/30 bg-card"}`}
                      data-testid={`thread-${t.id}`}>
                <div className="flex items-start gap-2">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${t.unread ? "bg-accent text-accent-foreground" : M.color}`}>
                    <Icon size={16} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className={`text-sm truncate ${t.unread ? "font-bold" : "font-medium"}`}>{t.title}</div>
                      {t.unread && <span className="w-2 h-2 rounded-full bg-accent shrink-0" />}
                      {t.readonly && <Lock size={10} className="text-muted-foreground shrink-0" />}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                      <span>{M.label}</span> · <span>{t.message_count || 0} msg</span> · <span>{(t.participant_ids||[]).length} membres</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Message pane */}
        <Card className="lg:col-span-2 p-0 flex flex-col overflow-hidden" data-testid="message-pane">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-8 text-center">
              Sélectionnez un fil à gauche pour lire les messages.
            </div>
          ) : (
            <>
              <div className="border-b border-border p-4 bg-muted/30">
                <div className="flex items-center gap-2">
                  <div className="font-heading font-semibold">{selected.title}</div>
                  {selected.readonly && <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/30"><Lock size={9} className="mr-1" /> lecture seule</Badge>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {(SCOPE_META[selected.scope] || SCOPE_META.direct).label} · {(selected.participant_ids||[]).length} membres
                </div>
              </div>
              <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[420px]">
                {messages.length === 0 ? (
                  <div className="text-center text-xs text-muted-foreground py-10">Aucun message.</div>
                ) : messages.map(m => {
                  const mine = m.author_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-lg p-3 ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                        {!mine && <div className="text-[10px] font-semibold opacity-80 mb-1 uppercase tracking-wide">{m.author_name} · {m.author_role}</div>}
                        <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                        <div className={`text-[9px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-border p-3 flex gap-2">
                {readonlyForMe ? (
                  <div className="flex-1 text-center text-xs text-muted-foreground py-3 flex items-center justify-center gap-2">
                    <Lock size={14} /> Fil en lecture seule — seule la direction peut y répondre.
                  </div>
                ) : (
                  <>
                    <Textarea rows={2} value={body} onChange={e => setBody(e.target.value)}
                              placeholder="Écrire un message… (Ctrl+Entrée pour envoyer)"
                              onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) { e.preventDefault(); send(); } }}
                              data-testid="input-msg-body" />
                    <Button onClick={send} className="bg-accent hover:bg-accent/90 text-accent-foreground self-end" data-testid="btn-send-msg">
                      <PaperPlaneRight size={16} />
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
